import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Mail, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtDate, formatApiError } from "@/lib/adminApi";
import { StatusBadge } from "@/components/admin/StatusBadge";

const FIELDS = [
  ["Email", (q) => q.email], ["Phone", (q) => q.phone], ["Project address", (q) => q.project_address],
  ["Rooms to decorate", (q) => q.rooms.map((r) => (r === "Other" ? `Other (${q.other_room})` : r)).join(", ")],
  ["Total number of rooms", (q) => q.total_rooms], ["Furniture & decor budget", (q) => q.budget],
  ["Existing items to keep", (q) => q.keep_items], ["Notes or special requirements", (q) => q.notes],
  ["Ideal completion date", (q) => q.completion_date], ["Submitted", (q) => fmtDate(q.created_at)], ["Inquiry ID", (q) => q.inquiry_id],
];

const Detail = ({ q, onBack, onResend, onDelete }) => (
  <div className="hx-fade" data-testid="inquiry-detail">
    <div className="hx-toolbar">
      <button className="hx-btn hx-btn--ghost hx-btn--sm" onClick={onBack} data-testid="inquiry-detail-back-button"><ArrowLeft size={14} /> All inquiries</button>
      <div className="hx-toolbar__group">
        {q.email_status !== "email_off" && <button className="hx-btn hx-btn--sm" onClick={() => onResend(q)} data-testid="inquiry-detail-resend-button"><Mail size={14} /> Resend email</button>}
        <button className="hx-btn hx-btn--ghost hx-btn--sm hx-btn--danger" onClick={() => onDelete(q)} data-testid="inquiry-detail-delete-button"><Trash2 size={14} /> Delete</button>
      </div>
    </div>
    <h2 className="hx-title" style={{ marginTop: 20 }} data-testid="inquiry-detail-name">{q.first_name} {q.last_name}</h2>
    <p style={{ marginTop: 8 }}><StatusBadge status={q.email_status} testId="inquiry-detail-email-status" /> {q.email_error && <span className="hx-muted" data-testid="inquiry-detail-email-error">{q.email_error}</span>}</p>
    <dl className="hx-kv">
      {FIELDS.map(([label, get]) => (
        <div key={label}><dt>{label}</dt><dd style={{ whiteSpace: "pre-wrap" }}>{get(q) || "—"}</dd></div>
      ))}
    </dl>
  </div>
);

export const InquiriesTab = () => {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      setItems((await adminApi.get("/admin/inquiries")).data);
    } catch (err) {
      if (err.response?.status !== 401) toast.error("Could not load inquiries");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resend = async (q) => {
    try {
      await adminApi.post(`/admin/inquiries/${q.inquiry_id}/resend`);
      toast.success("Email queued");
      setItems((l) => l.map((x) => (x.inquiry_id === q.inquiry_id ? { ...x, email_status: "pending", email_error: null } : x)));
      setTimeout(load, 6000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Resend failed"));
    }
  };

  if (!items) return <p className="hx-empty">Loading inquiries…</p>;
  const remove = async (q) => {
    if (!window.confirm(`Delete the inquiry from ${q.first_name} ${q.last_name}? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/inquiries/${q.inquiry_id}`);
      setItems((l) => l.filter((x) => x.inquiry_id !== q.inquiry_id));
      setOpenId(null);
      toast.success("Inquiry deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Delete failed"));
    }
  };
  const open = items.find((q) => q.inquiry_id === openId);
  if (open) return <Detail q={open} onBack={() => setOpenId(null)} onResend={resend} onDelete={remove} />;

  return (
    <div data-testid="inquiries-tab">
      <div className="hx-toolbar">
        <span className="hx-count" data-testid="inquiry-count">{items.length} inquir{items.length === 1 ? "y" : "ies"}</span>
        <button className="hx-btn hx-btn--ghost hx-btn--sm" onClick={load} data-testid="inquiry-refresh-button"><RefreshCw size={14} /> Refresh</button>
      </div>
      {items.length === 0 ? (
        <p className="hx-empty" data-testid="inquiry-empty-state">No inquiries yet.</p>
      ) : (
        <table className="hx-table hx-table--stack" data-testid="inquiry-table">
          <thead><tr><th>Name</th><th>Contact</th><th>Project address</th><th>Submitted</th><th>Email</th><th /></tr></thead>
          <tbody>
            {items.map((q) => (
              <tr key={q.inquiry_id} data-testid={`inquiry-row-${q.inquiry_id}`}>
                <td data-label="Name"><button className="hx-link" onClick={() => setOpenId(q.inquiry_id)}>{q.first_name} {q.last_name}</button></td>
                <td data-label="Contact"><div>{q.email}</div><div className="hx-muted">{q.phone}</div></td>
                <td data-label="Project address">{q.project_address}</td>
                <td data-label="Submitted" className="hx-muted">{fmtDate(q.created_at)}</td>
                <td data-label="Email"><StatusBadge status={q.email_status} testId={`inquiry-email-status-${q.inquiry_id}`} /></td>
                <td data-label="Actions">
                  <div className="hx-actions">
                    <button className="hx-btn hx-btn--ghost hx-btn--sm" onClick={() => setOpenId(q.inquiry_id)} data-testid={`inquiry-view-${q.inquiry_id}`}>View</button>
                    <button className="hx-btn hx-btn--ghost hx-btn--sm hx-btn--danger" onClick={() => remove(q)} aria-label={`Delete inquiry from ${q.first_name} ${q.last_name}`} data-testid={`inquiry-delete-${q.inquiry_id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
